import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Observable, catchError, concatMap, forkJoin, from, map, of, switchMap, throwError } from 'rxjs'

import {
  AoDocumentToUpload,
  AoDocumentUploadOutput,
  AoDocumentUploadOutputList,
  NetworkService,
  ToastService,
  requireDefined,
} from '@agroone-front/shared'
import { ALLOWED_FILE_MIME_TYPE, AoDocumentDto, Intervention, SaveIntervention, TPATask } from '@agroone/entities'
import { environment } from '@front-app-environments/environment'
import { FileService } from '../../../core/file/file.service'

type InterventionSaveOptions = { params: { taskId?: number } }

@Injectable({
  providedIn: 'root',
})
export class InterventionService {
  private interventionUrl = `${environment.apiUrl}${environment.interventions}`
  private interventionDocumentUrl = `${environment.apiUrl}${environment.documents}/interventions`

  public pictureList: File[] = []

  private http = inject(HttpClient)
  private fileService = inject(FileService)
  private translateService = inject(TranslateService)
  private toastService = inject(ToastService)
  private networkService = inject(NetworkService)

  private create(
    intervention: SaveIntervention | SaveIntervention[],
    options?: InterventionSaveOptions,
    documents?: AoDocumentUploadOutputList,
    pictures?: AoDocumentUploadOutputList,
  ): Observable<Intervention[]> {
    // The POST response is already complete; we only need to re-fetch an intervention
    // when documents/pictures were uploaded afterwards (they aren't part of that response).
    const hasFilesToUpload = this.hasFilesToUpload(documents, pictures)

    return this.http.post<Intervention[]>(this.interventionUrl, intervention, options).pipe(
      switchMap((created: Intervention[]) =>
        forkJoin(
          created.map((newInt) =>
            this.uploadNewDocuments(requireDefined(newInt.id, 'the intervention id'), documents).pipe(
              concatMap(() => this.savePicturesFromList(pictures, newInt.id)),
              switchMap(() =>
                hasFilesToUpload ? this.get(requireDefined(newInt.id, 'the intervention id')) : of(newInt),
              ),
              map((updated) => new Intervention(updated)),
            ),
          ),
        ),
      ),
      catchError((error) => {
        this.toastService.showToastError(this.translateService.instant('INTERVENTION.SEND_FAILURE'))
        return throwError(() => error)
      }),
    )
  }

  private update(
    intervention: SaveIntervention,
    options?: InterventionSaveOptions,
    documents?: AoDocumentUploadOutputList,
    pictures?: AoDocumentUploadOutputList,
  ): Observable<Intervention[]> {
    return this.http.put<Intervention>(`${this.interventionUrl}/${intervention.id}`, intervention, options).pipe(
      // 1) sync documents (delete/update/upload)
      concatMap((updatedInt: Intervention) =>
        this.syncDocuments(requireDefined(updatedInt.id, 'the intervention id'), documents).pipe(map(() => updatedInt)),
      ),
      // 2) upload pictures if any
      concatMap((updatedInt: Intervention) =>
        this.savePicturesFromList(pictures, updatedInt.id).pipe(map(() => updatedInt)),
      ),
      // 3) get fresh intervention
      switchMap((returnedIntervention: Intervention) =>
        this.get(requireDefined(returnedIntervention.id, 'the intervention id')),
      ),
      // 4) instantiate
      map((updated) => [new Intervention(updated)]),
      catchError((error) => {
        this.toastService.showToastError(this.translateService.instant('INTERVENTION.SEND_FAILURE'))
        return throwError(() => error)
      }),
    )
  }

  public save(
    intervention: SaveIntervention | SaveIntervention[],
    task?: TPATask,
    documents?: AoDocumentUploadOutputList,
    pictures?: AoDocumentUploadOutputList,
  ): Observable<Intervention[]> {
    const options: InterventionSaveOptions = { params: {} }
    if (task?.id) {
      options.params.taskId = task.id
    }
    // A batch (array) is always a creation; a single intervention with an id is an update.
    return !Array.isArray(intervention) && intervention?.id
      ? this.update(intervention, options, documents, pictures)
      : this.create(intervention, options, documents, pictures)
  }

  public uploadDocument(interventionId: number, file: File, description: string | undefined): Observable<Intervention> {
    const formData: FormData = new FormData()
    formData.append('file', file)
    if (description != null && description !== '') {
      formData.append('description', description)
    }

    return this.http.post<Intervention>(`${this.interventionDocumentUrl}/${interventionId}/documents`, formData)
  }

  public duplicate(sourceCropId: number, targetCropId: number): Observable<Intervention[]> {
    return this.http.post<Intervention[]>(`${this.interventionUrl}/crops/${sourceCropId}/duplicate`, { targetCropId })
  }

  public deleteDocument(interventionId: number, documentId: number): Observable<void> {
    return this.http.delete<void>(`${this.interventionDocumentUrl}/${interventionId}/documents/${documentId}`)
  }

  public updateDocumentDescription(
    interventionId: number,
    documentId: number,
    description: string | undefined,
  ): Observable<Intervention> {
    return this.http.put<Intervention>(`${this.interventionDocumentUrl}/${interventionId}/documents/${documentId}`, {
      description,
    })
  }

  private isToUpload(d: AoDocumentUploadOutput | AoDocumentDto): d is AoDocumentToUpload {
    // Only an AoDocumentUploadOutput carries a file
    return !!d && (d as { file?: unknown }).file instanceof File && !d.key
  }

  private isExisting(d: AoDocumentUploadOutput | AoDocumentDto): d is AoDocumentDto {
    return !!d && !!d.id && !!d.key
  }

  // Whether create() will trigger document/picture uploads (and thus needs a re-fetch).
  private hasFilesToUpload(documents?: AoDocumentUploadOutputList, pictures?: AoDocumentUploadOutputList): boolean {
    const hasDocuments = ((documents ?? []) as AoDocumentUploadOutput[]).some(
      (d) => this.isToUpload(d) && !!d.file?.type && ALLOWED_FILE_MIME_TYPE.includes(d.file.type),
    )
    const hasPictures = this.extractImageFiles(pictures).length > 0 || (this.pictureList?.length ?? 0) > 0
    return hasDocuments || hasPictures
  }

  private uploadNewDocuments(
    interventionId: number,
    documents?: AoDocumentUploadOutputList,
  ): Observable<Intervention[] | null> {
    const toUpload = ((documents ?? []) as AoDocumentUploadOutput[])
      .filter((d) => this.isToUpload(d))
      .filter((d) => !!d.file?.type && ALLOWED_FILE_MIME_TYPE.includes(d.file.type))
    if (!toUpload.length) return of(null)
    return forkJoin(toUpload.map((d) => this.uploadDocument(interventionId, d.file, d.description)))
  }

  private syncDocuments(interventionId: number, documents?: AoDocumentUploadOutputList): Observable<unknown> {
    if (!documents) return of(null)
    return this.get(interventionId).pipe(
      switchMap((current) => {
        const base = (current?.documents ?? ([] as AoDocumentDto[])).filter(
          (b) => !!b.mimeType && ALLOWED_FILE_MIME_TYPE.includes(b.mimeType),
        )
        const docsOnly = ((documents ?? []) as (AoDocumentUploadOutput | AoDocumentDto)[]).filter((d) => {
          if (this.isToUpload(d)) {
            return !!d.file?.type && ALLOWED_FILE_MIME_TYPE.includes(d.file.type)
          }
          if (this.isExisting(d)) {
            return base.some((b) => b.id === d.id)
          }
          return false
        })

        const toDelete = base.filter((b) => !docsOnly.some((n) => n.id === b.id))
        const toUpdate = docsOnly.filter(
          (d) => this.isExisting(d) && base.some((b) => b.id === d.id),
        ) as AoDocumentUploadOutput[]
        const toUpload = docsOnly.filter((d) => this.isToUpload(d))

        const del$: Observable<unknown> = toDelete.length
          ? forkJoin(toDelete.map((d) => this.deleteDocument(interventionId, requireDefined(d.id, 'the document id'))))
          : of(null)
        const upd$: Observable<unknown> = toUpdate.length
          ? forkJoin(toUpdate.map((d) => this.updateDocumentDescription(interventionId, d.id, d.description)))
          : of(null)
        const up$: Observable<unknown> = toUpload.length
          ? forkJoin(toUpload.map((d) => this.uploadDocument(interventionId, d.file, d.description)))
          : of(null)

        // ordre: delete -> update -> upload
        return del$.pipe(
          concatMap(() => upd$),
          concatMap(() => up$),
          catchError((_e) => {
            this.toastService.showToastError(this.translateService.instant('INTERVENTION.SEND_FAILURE'))
            return of(null)
          }),
        )
      }),
    )
  }

  // Extract image files from AoDocumentUploadOutputList
  private extractImageFiles(pictures?: AoDocumentUploadOutputList): File[] {
    const arr = (pictures ?? []) as AoDocumentUploadOutput[]
    return arr
      .filter((p): p is AoDocumentToUpload => !!p && p.file instanceof File)
      .map((p) => p.file)
      .filter((f) => !!f && typeof f.type === 'string' && f.type.startsWith('image/'))
  }

  // Upload pictures to /interventions/{completedBy#date}/pictures
  private savePicturesFromList(
    pictures?: AoDocumentUploadOutputList,
    interventionId?: number,
  ): Observable<Intervention[]> {
    const files: File[] = this.extractImageFiles(pictures).concat(this.pictureList || [])
    if (!files.length || !interventionId) return of([])

    return from(this.fileService.compressFiles(files)).pipe(
      switchMap((compressed) =>
        forkJoin((compressed || []).map((picture) => this.uploadDocument(interventionId, picture, ''))),
      ),
    )
  }

  /**
   * Return interevntion item
   *
   * @param interventionId
   * @returns
   */
  public get(interventionId: number): Observable<Intervention> {
    return this.http.get<Intervention>(`${this.interventionUrl}/${interventionId}`)
  }

  /**
   * display signed images previews
   *
   * @param interventionId
   * @param allowedMimeTypes
   * @returns
   */
  public getInterventionDocumentSignedUrls(interventionId: number, allowedMimeTypes: string[]): Observable<string[]> {
    // passing mime types as following exemple: allowedMimeTypes=image/png,image/jpeg,application/pdf
    const params: Record<string, string> = {}
    if (allowedMimeTypes?.length) {
      params.allowedMimeTypes = allowedMimeTypes.join(',')
    }
    return this.http.get<string[]>(`${this.interventionDocumentUrl}/${interventionId}/documents/signed-urls`, {
      params,
    })
  }

  public getInterventionDocument(interventionId: number, documentId: number): Observable<string> {
    return this.http.get(`${this.interventionDocumentUrl}/${interventionId}/documents/${documentId}`, {
      responseType: 'text',
    })
  }

  /**
   * Return interventions by crop id
   *
   * @param cropId
   * @returns
   */
  public getByCropId(cropId: number): Observable<Intervention[]> {
    return from(
      this.networkService.getAllFromPaginated<Intervention>(`${this.interventionUrl}?filter=cropId=${cropId}`, {
        params: {
          noPagination: true,
          includeCropDetails: false,
        },
      }),
    ).pipe(map((interventions) => interventions?.map((intervention) => new Intervention(intervention)) ?? []))
  }

  /**
   * Delete intervention item by id
   *
   * @param id
   * @returns
   */
  public delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.interventionUrl}/${id}`)
  }

  public validate(id: number): Observable<Intervention> {
    return this.http.put<Intervention>(`${this.interventionUrl}/${id}/validate`, {})
  }
}
